"use client";

import { useMemo, useState } from "react";
import type { PaygCalculatorContent } from "@/lib/content/payg-calculator";
import {
  paygDefaultRequests,
  paygServiceOrder,
  paygSliderBounds,
  paygUnitPrices,
  type PaygServiceId,
} from "@/lib/content/pricing/payg-rates";
import { useLocale } from "@/lib/i18n/locale-context";
import { cn, formatCurrency } from "@/lib/utils";

function getRequestsLabel(template: string, count: number, locale: string) {
  return template.replace(
    "{count}",
    count.toLocaleString(
      locale === "ar"
        ? "ar"
        : locale === "zh"
          ? "zh-CN"
          : locale === "tr"
            ? "tr-TR"
            : locale === "de"
              ? "de-DE"
              : locale === "es"
                ? "es-ES"
                : locale === "fr"
                  ? "fr-FR"
                  : locale === "pt"
                    ? "pt-BR"
                    : locale === "ru"
                      ? "ru-RU"
                      : "en-US",
    ),
  );
}

function ServiceModuleCard({
  label,
  requests,
  requestsLabel,
  sliderMinLabel,
  sliderMaxLabel,
  onChange,
}: {
  label: string;
  requests: number;
  requestsLabel: string;
  sliderMinLabel: string;
  sliderMaxLabel: string;
  onChange: (value: number) => void;
}) {
  const { min, max, step } = paygSliderBounds;
  const progress = Math.round(((requests - min) / (max - min)) * 100);

  return (
    <div
      className={cn(
        "service-mesh-surface flex h-full flex-col rounded-2xl border border-line p-5 transition-opacity",
        requests === 0 && "opacity-60",
      )}
    >
      <p className="text-sm font-semibold text-foreground">{label}</p>

      <p className="mt-3 text-xs leading-5 text-muted-foreground">
        {requestsLabel}
      </p>

      <div className="mt-4" dir="ltr">
        <input
          type="range"
          className="roi-range w-full"
          min={min}
          max={max}
          step={step}
          value={requests}
          style={{ "--range-progress": `${progress}%` } as React.CSSProperties}
          aria-label={label}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={requests}
          onChange={(event) => onChange(Number(event.target.value))}
        />
        <div className="mt-2 flex items-center justify-between text-xs tabular-nums text-muted-foreground">
          <span>{sliderMinLabel}</span>
          <span>{sliderMaxLabel}</span>
        </div>
      </div>
    </div>
  );
}

export default function PaygCalculatorSection({
  content,
}: {
  content: PaygCalculatorContent;
}) {
  const locale = useLocale();

  const [requestsByService, setRequestsByService] =
    useState(paygDefaultRequests);

  const serviceLabels = useMemo(
    () =>
      Object.fromEntries(
        content.paygCalculatorServices.map((service) => [
          service.id,
          service.label,
        ]),
      ) as Record<PaygServiceId, string>,
    [content.paygCalculatorServices],
  );

  const totalMonthlyCost = useMemo(
    () =>
      paygServiceOrder.reduce(
        (total, serviceId) =>
          total + requestsByService[serviceId] * paygUnitPrices[serviceId],
        0,
      ),
    [requestsByService],
  );

  const { paygCalculatorSteps: steps, paygCalculatorUi: ui } = content;

  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-base font-semibold text-foreground">
          {steps.modulesTitle}
        </h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {paygServiceOrder.map((serviceId) => (
            <ServiceModuleCard
              key={serviceId}
              label={serviceLabels[serviceId]}
              requests={requestsByService[serviceId]}
              requestsLabel={getRequestsLabel(
                ui.requestsPerMonth,
                requestsByService[serviceId],
                locale,
              )}
              sliderMinLabel={ui.sliderMinLabel}
              sliderMaxLabel={ui.sliderMaxLabel}
              onChange={(value) =>
                setRequestsByService((current) => ({
                  ...current,
                  [serviceId]: value,
                }))
              }
            />
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-base font-semibold text-foreground">
          {steps.totalTitle}
        </h2>
        <div className="mt-4 rounded-xl bg-foreground/[0.03] px-5 py-4 md:px-6 md:py-5">
          <p className="text-sm font-medium text-muted-foreground">
            {steps.totalLabel}
          </p>
          <p
            className="mt-2 text-[clamp(2rem,4vw,2.75rem)] leading-none font-semibold tracking-tight tabular-nums text-foreground"
            dir="ltr"
          >
            {formatCurrency(totalMonthlyCost)}
          </p>
        </div>
      </section>

      <section className="border-t border-line pt-8">
        <h2 className="text-base font-semibold text-foreground">
          {steps.salesTitle}
        </h2>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground">
          {steps.salesBeforeLink}
          <a
            href={steps.salesLinkHref}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-foreground underline decoration-foreground/20 underline-offset-4 transition-colors hover:decoration-foreground/50"
          >
            {steps.salesLinkLabel}
          </a>
          {steps.salesAfterLink}
        </p>
      </section>
    </div>
  );
}
